import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa';
import PaginationBar from '@/components/PaginationBar';
import SortHeader, { type SortDirection } from '@/components/SortHeader';
import InventoryNav from './InventoryNav';
import { formatBytes, formatCount, formatDateTime } from './format';
import {
  getCrawlLibraries,
  getCrawlRuns,
  type CrawlLibraryDto,
  type CrawlRunDto,
  type PagedResultDto,
  type SiteScope,
} from '@/api/sharepointInventoryApi';

const PAGE_SIZE = 25;

const statusVariant = (status: string) => {
  switch (status) {
    case 'Complete': return 'success';
    case 'Failed': return 'danger';
    case 'Running': return 'info';
    case 'Cancelled': return 'secondary';
    default: return 'secondary';
  }
};

const CrawlHealthPage: React.FC = () => {
  const [data, setData] = useState<PagedResultDto<CrawlRunDto> | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [siteScope, setSiteScope] = useState<SiteScope>('All');
  const [sortBy, setSortBy] = useState('lastActivityDt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<number | null>(null);
  const [libraries, setLibraries] = useState<CrawlLibraryDto[]>([]);
  const [librariesLoading, setLibrariesLoading] = useState(false);
  const loadRequest = useRef(0);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    setLoading(true);
    try {
      const result = await getCrawlRuns({
        status: status || undefined,
        siteScope,
        skip: (page - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
        sortBy,
        sortDirection,
        search: debouncedSearch || undefined,
      });
      if (request !== loadRequest.current) return;
      setData(result);
      setError(null);
    } catch {
      if (request === loadRequest.current) setError('Failed to load crawl history.');
    } finally {
      if (request === loadRequest.current) setLoading(false);
    }
  }, [page, status, siteScope, sortBy, sortDirection, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const onSort = (column: string) => {
    if (sortBy === column) {
      setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const toggle = async (crawlId: number) => {
    if (expanded === crawlId) {
      setExpanded(null);
      return;
    }
    setExpanded(crawlId);
    setLibrariesLoading(true);
    try {
      setLibraries(await getCrawlLibraries(crawlId));
    } catch {
      setLibraries([]);
    } finally {
      setLibrariesLoading(false);
    }
  };

  return (
    <Container fluid className="py-3">
      <h3 className="mb-1">Crawl Health</h3>
      <div className="text-muted small mb-3">
        History of file inventory crawls. Crawls are started outside this application.
        {' '}Latest activity first by default, including resumed crawls. Click a header to sort.
        {' '}Total Storage is the latest stored SharePoint site total, including version history, not this crawl&apos;s file total.
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <InventoryNav />

      <Card>
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Form.Control
              type="search"
              aria-label="Search crawls by site title or URL"
              placeholder="Search site title or URL"
              style={{ width: 300, maxWidth: '100%' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Form.Select
              aria-label="Site scope"
              style={{ width: 240, maxWidth: '100%' }}
              value={siteScope}
              onChange={(e) => {
                setSiteScope(e.target.value as SiteScope);
                setPage(1);
              }}
            >
              <option value="All">SharePoint + OneDrive</option>
              <option value="SharePoint">SharePoint only</option>
              <option value="OneDrive">OneDrive only</option>
            </Form.Select>
            <Form.Select
              style={{ maxWidth: 220 }}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="Running">Running</option>
              <option value="Complete">Complete</option>
              <option value="Failed">Failed</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
            </Form.Select>
            <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>
              <FaSyncAlt className="me-1" />Refresh
            </Button>
          </div>

          {loading && !data ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Table hover size="sm" responsive>
                <thead>
                  <tr>
                    <th style={{ width: 30 }} />
                    <th><SortHeader label="Site" column="siteTitle" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Status" column="status" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Stage" column="stage" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th className="text-end"><SortHeader label="Files" column="filesProcessed" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th className="text-end"><SortHeader label="Libraries" column="librariesProcessed" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th className="text-end"><SortHeader label="Total Storage" column="siteStorageUsedMb" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Started" column="startedDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Last checkpoint" column="lastCheckpointDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Completed" column="completedDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                    <th><SortHeader label="Last activity" column="lastActivityDt" sortBy={sortBy} sortDirection={sortDirection} onSort={onSort} /></th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((c) => (
                    <React.Fragment key={c.sharePointFileCrawlId}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => toggle(c.sharePointFileCrawlId)}>
                        <td>{expanded === c.sharePointFileCrawlId ? <FaChevronDown /> : <FaChevronRight />}</td>
                        <td>
                          <Link
                            to={`/admin/sharepoint/inventory/sites/${c.siteCollectionId}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {c.siteTitle || c.siteUrl}
                          </Link>
                          {c.isOneDrive && <Badge bg="info" className="ms-1">OneDrive</Badge>}
                          <div className="text-muted small">{c.siteUrl}</div>
                        </td>
                        <td>
                          <Badge bg={statusVariant(c.status)}>{c.status}</Badge>
                          {c.librariesPending > 0 && c.status === 'Complete' && (
                            <Badge bg="warning" className="ms-1" title="Libraries left unscanned in this crawl">
                              {c.librariesPending} unscanned
                            </Badge>
                          )}
                        </td>
                        <td>{c.stage}</td>
                        <td className="text-end">{formatCount(c.filesProcessed)}</td>
                        <td className="text-end">{formatCount(c.librariesProcessed)}</td>
                        <td className="text-end">{formatBytes(c.siteStorageUsedMb == null ? null : c.siteStorageUsedMb * 1024 * 1024)}</td>
                        <td className="small lh-sm">{formatDateTime(c.startedDt)}</td>
                        <td className="small lh-sm">{formatDateTime(c.lastCheckpointDt)}</td>
                        <td className="small lh-sm">{formatDateTime(c.completedDt)}</td>
                        <td className="small lh-sm">{formatDateTime(c.lastActivityDt)}</td>
                      </tr>
                      {c.errorMessage && (
                        <tr>
                          <td />
                          <td colSpan={10} className="text-danger small">{c.errorMessage}</td>
                        </tr>
                      )}
                      {expanded === c.sharePointFileCrawlId && (
                        <tr>
                          <td />
                          <td colSpan={10}>
                            {librariesLoading ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              <Table size="sm" bordered className="mb-0">
                                <thead>
                                  <tr>
                                    <th>Library</th>
                                    <th>Status</th>
                                    <th className="text-end">Files</th>
                                    <th>Completed</th>
                                    <th>Error</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {libraries.map((l) => (
                                    <tr key={l.sharePointLibraryId}>
                                      <td>{l.title}</td>
                                      <td><Badge bg={statusVariant(l.status)}>{l.status}</Badge></td>
                                      <td className="text-end">{formatCount(l.filesProcessed)}</td>
                                      <td className="small lh-sm">{formatDateTime(l.completedDt)}</td>
                                      <td className="text-danger small">{l.errorMessage}</td>
                                    </tr>
                                  ))}
                                  {libraries.length === 0 && (
                                    <tr><td colSpan={5} className="text-muted">No libraries recorded.</td></tr>
                                  )}
                                </tbody>
                              </Table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {data?.items.length === 0 && (
                    <tr><td colSpan={11} className="text-muted">No crawls recorded yet.</td></tr>
                  )}
                </tbody>
              </Table>
              {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CrawlHealthPage;
